import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { supabase } from "../../supabaseClient.js";
import useAlert from '../../alert/useAlert.js';
import { secureUnhash } from '../../utils/patientIdCipher.js';

const toISODate = (d) => new Date(d).toISOString();
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

const dateRangeFromPreset = (preset, customFrom, customTo) => {
  const now = new Date();
  if (preset === 'custom' && customFrom && customTo) {
    return { start: startOfDay(customFrom), end: endOfDay(customTo) };
  }
  if (preset === 'month') {
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return { start: startOfDay(start), end: endOfDay(now) };
  }
  if (preset === 'quarter') {
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return { start: startOfDay(start), end: endOfDay(now) };
  }
  if (preset === 'year') {
    const start = new Date();
    start.setDate(start.getDate() - 365);
    return { start: startOfDay(start), end: endOfDay(now) };
  }
  // default week
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return { start: startOfDay(start), end: endOfDay(now) };
};

const decryptText = async (value) => {
  if (!value) return '';
  try {
    const { data, error } = await supabase.rpc('decrypt_text', { encrypted_data: value });
    if (!error) return data?.result ?? data ?? '';
  } catch (err) {
    console.error('decrypt_text error:', err);
  }
  return value || '';
};

const normalizeDigits = (value) => String(value ?? '').replace(/\D/g, '');

const decryptArray = async (arr) => {
  if (!Array.isArray(arr)) return [];
  return Promise.all(arr.map((v) => decryptText(v)));
};

const unhashPatientId = async (value) => {
  if (!value) return '';
  const decrypted = await decryptText(value);
  let unhashed = decrypted;
  try {
    unhashed = await secureUnhash(decrypted, process.env.REACT_APP_PATIENT_ID_SECRET);
  } catch {
    // fallback keep decrypted
  }
  return normalizeDigits(unhashed);
};

const formatMoney = (cents) => {
  const val = (Number(cents || 0) / 100);
  return `HK$ ${val.toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const isTrue = (v) => v === true || v === 'true' || v === 'paid' || v === 'succeeded';

// A refund has been issued on this appointment (patient refunded; if the doctor was
// already paid out, their share is reversed from the transfer). `refund_status`
// defaults to 'none' and is set to 'succeeded' by the backend refund flow.
const isRefunded = (a) => {
  const rs = String(a?.refund_status || '').toLowerCase();
  return rs !== '' && rs !== 'none';
};

const getBillingStatus = (a) => {
  if (!a) return '—';
  const paid = isTrue(a.payment_status);
  const scheduled = !!a.payment_scheduled_transfer;
  const transferred = !!a.payment_transferred;

  // Only payable (paid + scheduled) appointments appear in the billing list.
  if (!(paid && scheduled)) return '—';
  // A refund overrides Paid/Pending — the money was returned to the patient (and
  // any doctor payout reversed), so it must not keep showing as earned.
  if (isRefunded(a)) return 'Refunded';
  return transferred ? 'Paid' : 'Pending';
};

export const useBillingsData = () => {
  const { userDetails } = useSelector(
    (state) => ({ userDetails: state.globalState.userDetails }),
    shallowEqual
  );
  const role = useSelector((state) => state.globalState.role, shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const { setAlert } = useAlert();
  const hasMounted = useRef(false);

  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  // doctorId -> { userId, name } for every therapist in the current org.
  const [doctorMap, setDoctorMap] = useState({});
  const doctorMapCache = useRef({ orgId: null, map: {} });

  const isAdmin = String(role || '').toLowerCase() === 'admin';
  const currentUserId = userDetails?.id || null;

  const [timeRange, setTimeRange] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchText, setSearchText] = useState('');

  const [page, setPage] = useState(1);
  const pageSize = 5;

  const { start, end } = useMemo(
    () => dateRangeFromPreset(timeRange, customFrom, customTo),
    [timeRange, customFrom, customTo]
  );

  // Load the org's therapists (doctor row -> user account name) once per org so
  // each billing row can be tagged with who ran the session.
  const loadDoctorMap = async (orgId) => {
    if (doctorMapCache.current.orgId === orgId) return doctorMapCache.current.map;

    const { data: doctors, error: docErr } = await supabase
      .from('doctors')
      .select('id, user_id')
      .eq('team_id', orgId);
    if (docErr) throw docErr;

    const userIds = [...new Set((doctors || []).map((d) => d.user_id).filter(Boolean))];
    const names = {};
    if (userIds.length > 0) {
      const { data: users, error: userErr } = await supabase
        .from('user_accounts')
        .select('id, name')
        .in('id', userIds);
      if (userErr) {
        console.error('Billings: failed to load therapist names', userErr);
      } else {
        await Promise.all(
          (users || []).map(async (u) => {
            names[u.id] = await decryptText(u.name);
          })
        );
      }
    }

    const map = {};
    (doctors || []).forEach((d) => {
      map[d.id] = { userId: d.user_id || null, name: names[d.user_id] || '' };
    });
    doctorMapCache.current = { orgId, map };
    return map;
  };

  const fetchBillings = async () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    try {
      const map = await loadDoctorMap(currentOrg.id);
      setDoctorMap(map);

      // Members only ever see sessions run under their own doctor profile(s);
      // admins see every therapist in the org.
      const ownDoctorIds = Object.entries(map)
        .filter(([, d]) => d.userId && d.userId === currentUserId)
        .map(([id]) => id);

      if (!isAdmin && ownDoctorIds.length === 0) {
        setAppointments([]);
        setPage(1);
        return;
      }

      let query = supabase
        .from('appointments')
        .select(`
          id, created_at, patient_id, patient_name, appointment_starttime, appointment_endtime,
          payment_status, payment_total_amount_cents, payment_platform_fee_cents,
          payment_scheduled_transfer, payment_transferred, category,
          doctor_id, stripe_event_id,
          appointment_status, refund_status, refunded_at, refund_amount_cents
        `)
        .eq('team_id', currentOrg.id)
        .gte('created_at', toISODate(start))
        .lte('created_at', toISODate(end));

      if (!isAdmin) query = query.in('doctor_id', ownDoctorIds);

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const decrypted = await Promise.all(
        (data || []).map(async (a) => ({
          ...a,
          patient_name: await decryptText(a.patient_name),
          category: await decryptText(a.category),
        }))
      );
      setAppointments(decrypted);
      setPage(1);
    } catch (err) {
      console.error('Billings fetch error:', err);
      setAlert('Failed to load billing data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getPatientDiagnosis = useCallback(async (appointmentPatientId) => {
    if (!appointmentPatientId) return [];
    try {
        const unhashedKey = await unhashPatientId(appointmentPatientId);

        const { data: rows, error } = await supabase.rpc('get_patient_diagnosis_by_phone', {
            user_phone_full: unhashedKey
        });

        if (error) throw error;

        const diagnosis = await decryptArray(rows?.[0]?.diagnosis);
        return diagnosis || [];
    } catch (err) {
        console.error('getPatientDiagnosis error:', err);
        throw new Error('Could not load the original diagnosis.');
    }
  }, []);

  useEffect(() => {
    if (!hasMounted.current && currentOrg) {
      fetchBillings();
      hasMounted.current = true;
    }
  }, [currentOrg]);

  useEffect(() => {
    if (currentOrg) fetchBillings();
  }, [timeRange, customFrom, customTo, currentOrg, isAdmin, currentUserId]);

  // Who ran this session, from the viewer's perspective: "Me" for the signed-in
  // therapist's own sessions, otherwise the therapist's display name.
  const getTherapistTag = useCallback((row) => {
    const d = row?.doctor_id ? doctorMap[row.doctor_id] : null;
    if (!d) return { label: 'Unassigned', isMe: false };
    if (d.userId && d.userId === currentUserId) return { label: 'Me', isMe: true };
    return { label: d.name || 'Therapist', isMe: false };
  }, [doctorMap, currentUserId]);


  const getDoctorProfile = useCallback(async (doctorId) => {
    if (!doctorId) return null;
    try {
      const { data: doctor, error: docErr } = await supabase
        .from('doctors')
        .select('user_id')
        .eq('id', doctorId)
        .single();

      if (docErr || !doctor?.user_id) return null;

      const { data: user, error: userErr } = await supabase
        .from('user_accounts')
        .select(`
          name, practice_title, registered_number_org, registered_number,
          phone_number, secondary_email, address
        `)
        .eq('id', doctor.user_id)
        .single();

      if (userErr) return null;

      return {
        name: await decryptText(user.name),
        practice_title: await decryptText(user.practice_title),
        registered_number_org: await decryptText(user.registered_number_org),
        registered_number: await decryptText(user.registered_number),
        phone_number: await decryptText(user.phone_number),
        secondary_email: await decryptText(user.secondary_email),
        address: await decryptText(user.address),
      };
    } catch (err) {
      console.error('getDoctorProfile error:', err);
      return null;
    }
  }, []);


  const filteredAppointments = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return appointments.filter((a) => {
      const status = getBillingStatus(a);
      if (status === '—') return false;
      if (statusFilter !== 'All' && status !== statusFilter) return false;
      if (!q) return true;
      return (
        String(a.id || '').toLowerCase().includes(q) ||
        String(a.patient_name || '').toLowerCase().includes(q) ||
        getTherapistTag(a).label.toLowerCase().includes(q)
      );
    });
  }, [appointments, statusFilter, searchText, getTherapistTag]);

  const totals = useMemo(() => {
    let total = 0;
    let paid = 0;
    let pending = 0;
    let refunded = 0;
    let totalCount = 0;
    let paidCount = 0;
    let pendingCount = 0;
    let refundedCount = 0;

    filteredAppointments.forEach((a) => {
      const paidOk = isTrue(a.payment_status);
      const scheduled = !!a.payment_scheduled_transfer;
      const transferred = !!a.payment_transferred;
      const net = Number((a.payment_total_amount_cents || 0) - (a.payment_platform_fee_cents || 0));

      if (!(paidOk && scheduled)) return;

      // Refunded earnings were clawed back — track them separately and keep them out
      // of Total/Paid/Pending so the headline figures reflect real earnings.
      if (isRefunded(a)) {
        refunded += net;
        refundedCount += 1;
        return;
      }

      total += net;
      totalCount += 1;
      if (transferred) {
        paid += net;
        paidCount += 1;
      } else {
        pending += net;
        pendingCount += 1;
      }
    });

    return {
      total, paid, pending, refunded,
      totalCount, paidCount, pendingCount, refundedCount,
    };
  }, [filteredAppointments]);

  const paged = useMemo(() => {
    const startIdx = (page - 1) * pageSize;
    return filteredAppointments.slice(startIdx, startIdx + pageSize);
  }, [filteredAppointments, page]);

  const totalPages = Math.max(1, Math.ceil(filteredAppointments.length / pageSize));

  return {
    loading,
    role,
    isAdmin,
    userDetails,
    currentOrg,
    getTherapistTag,

    timeRange,
    setTimeRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    statusFilter,
    setStatusFilter,
    searchText,
    setSearchText,

    appointments: filteredAppointments,
    paged,
    page,
    setPage,
    totalPages,
    totals,

    formatMoney,
    getBillingStatus,
    getDoctorProfile,
    getPatientDiagnosis
  };
};

export default useBillingsData;
